import { styles } from "../styles";

// Shared shell for quadrant mini-views. Wraps the standard tile card
// chrome around a small header + up-to-N row list + empty state +
// optional footer hint. Whole card is clickable -- per-row click-through
// is future scope; for now any tap opens the underlying page.
//
// Props:
//   title       — strong header (left)
//   badge       — small text or number rendered right of the title (e.g.
//                 "8 active") — undefined to hide. May also be a node
//                 (e.g. the Margin tile's 30/60/90 toggle).
//   rows        — array of { label, sub?, right?, rightColor?, data? }
//                 objects. label and sub truncate with ellipsis; right
//                 is the right-aligned value (status badge, $ amount,
//                 days). `data` is passed back to onRowClick.
//   empty       — text shown when rows is empty (default "Nothing here")
//   footer      — small footer hint (default "View all →"); false to hide
//   onClick     — open the underlying page
//   onRowClick  — optional (rowData, index) handler. When set, each row
//                 becomes its own clickable button (stopPropagation), so
//                 row taps navigate to a SPECIFIC item rather than the
//                 list page. Card-level onClick still fires on header /
//                 footer / empty area.

export default function MiniViewCard({
  title,
  badge,
  rows = [],
  empty,
  footer,
  onClick,
  onRowClick,
  children,
}) {
  const hasRows = rows && rows.length > 0;
  return (
    <div
      style={{
        ...styles.card,
        cursor: onClick ? "pointer" : "default",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        padding: "12px",
        minHeight: "180px",
      }}
      onClick={onClick}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: "8px",
        }}
      >
        <strong style={{ fontSize: "14px" }}>{title}</strong>
        {badge !== undefined && badge !== null && badge !== "" && (
          <span
            style={{
              fontSize: "11px",
              color: "#5a6470",
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {badge}
          </span>
        )}
      </div>

      {children}

      {!children && !hasRows && (
        <div
          style={{
            fontSize: "12px",
            color: "#888",
            padding: "12px 0",
            flex: 1,
            display: "flex",
            alignItems: "center",
          }}
        >
          {empty || "Nothing here yet"}
        </div>
      )}

      {!children && hasRows && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
          }}
        >
          {rows.map((r, i) => {
            const rowInner = (
              <>
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      color: "#222",
                    }}
                  >
                    {r.label}
                  </div>
                  {r.sub && (
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#888",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {r.sub}
                    </div>
                  )}
                </div>
                {r.right !== undefined &&
                  r.right !== null &&
                  r.right !== "" && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: r.rightColor || "#444",
                        fontWeight: 600,
                        flexShrink: 0,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {r.right}
                    </div>
                  )}
              </>
            );

            const baseStyle = {
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "12.5px",
              padding: "5px 0",
              borderBottom:
                i < rows.length - 1 ? "1px solid #f0f0f0" : "none",
            };

            if (onRowClick) {
              return (
                <button
                  key={i}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRowClick(r.data, i);
                  }}
                  style={{
                    ...baseStyle,
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    width: "100%",
                  }}
                >
                  {rowInner}
                </button>
              );
            }
            return (
              <div key={i} style={baseStyle}>
                {rowInner}
              </div>
            );
          })}
        </div>
      )}

      {footer !== false && onClick && (
        <div
          style={{
            fontSize: "11px",
            color: "#888",
            marginTop: "auto",
            paddingTop: "4px",
          }}
        >
          {footer || "View all →"}
        </div>
      )}
    </div>
  );
}
